import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, PenTool, Code2, Server, Wrench, LifeBuoy } from "lucide-react";
import { Browser3D } from "./Browser3D";

const trustItems = [
  { icon: PenTool, label: "Design" },
  { icon: Code2, label: "Development" },
  { icon: Server, label: "Hosting" },
  { icon: Wrench, label: "Maintenance" },
  { icon: LifeBuoy, label: "Support" },
];

const HEADLINE = [
  { text: "We Build Websites", grad: false },
  { text: "That Work For", grad: true },
  { text: "Your Business.", grad: true },
];

const Magnetic = ({ children }) => {
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 160, damping: 14, mass: 0.3 });
  const y = useSpring(0, { stiffness: 160, damping: 14, mass: 0.3 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.28);
    y.set((e.clientY - r.top - r.height / 2) * 0.28);
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={() => { x.set(0); y.set(0); }} style={{ x, y }} className="inline-block">
      {children}
    </motion.div>
  );
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={sectionRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grid-bg pt-28 pb-20 bg-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,85,255,0.06),_transparent_55%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white to-transparent" aria-hidden="true" />
      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-16 lg:gap-10 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 soft-shadow rounded-full px-4 py-1.5 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono2 text-[11px] tracking-widest uppercase text-slate-500">Design · Build · Host · Manage</span>
          </motion.div>
          <h1 aria-label="We Build Websites That Work For Your Business.">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1" aria-hidden="true">
                <motion.span
                  className={`block text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] ${line.grad ? "text-gradient-blue font-medium" : "text-slate-900"}`}
                  initial={{ y: "112%", rotate: 2.5 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{ duration: 0.95, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            WebStudios designs, builds, hosts and manages professional websites — giving you everything you need to establish and maintain a powerful online presence.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.68 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <Magnetic>
              <a href="#contact" data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200 glow-blue group">
                Get a Free Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </Magnetic>
            <a href="#services" data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-medium px-7 py-3.5 rounded-full transition-[border-color,background-color,color] duration-200">
              Explore Our Services
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.85 }} className="mt-10">
            <p className="text-sm text-slate-500 mb-4">From first design to ongoing management — we've got your website covered.</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {trustItems.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Icon className="w-3.5 h-3.5 text-[#0055FF]" /> {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: visualY }}
          className="hidden md:block"
        >
          <Browser3D />
        </motion.div>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2" aria-hidden="true">
        <div className="w-6 h-10 rounded-full border border-slate-300 flex justify-center pt-2">
          <div className="w-1 h-2 rounded-full bg-[#0055FF] scroll-dot" />
        </div>
      </div>
    </section>
  );
};
